/**
 * BatteryPictogram — 건전지 표식의 **큰 그림** (SC-023 · 수익모델 §4.3).
 *
 * 후원의 보상은 표식뿐이다. 그런데 지금까지 그 표식은 20px 아이콘 하나에 색만
 * 바뀌는 것이라 **"내가 몇 개째인지"가 안 보였다** — 개수는 옆의 글자만 알았다.
 * 열 장짜리 픽토그램이 그 일을 한다: 차오르고, 넘치고, 터진다.
 *
 * ⚠ **작은 자리에는 쓰지 말 것.** 픽셀 격자가 촘촘해 20~24px 로 줄이면 뭉갠다.
 *   내 정보 목록 줄처럼 글자에 딸려 붙는 자리는 픽셀 아이콘(Icon "Battery")이
 *   맞다. 그래서 표식은 자리에 따라 두 몸을 갖는다 —
 *     목록 20px 아이콘  →  시트 96px 그림  →  축하 160px 그림
 *   열수록 커진다.
 * ⚠ **0개면 아무것도 안 그린다** (batteryLevel 이 null). '건전지 0개' 같은
 *   부재 표시는 만들지 않는다.
 * ⚠ 그림은 **장식**이다. 개수는 늘 옆의 글자가 말하므로 alt 를 비운다 —
 *   여기에 개수를 또 넣으면 스크린리더가 같은 말을 두 번 읽는다.
 */
import { batteryLevel } from "../../data/donation";
import styles from "./BatteryPictogram.module.css";

import level02 from "../../assets/pictograms/peeka-battery-level-02-front.png";
import level03 from "../../assets/pictograms/peeka-battery-level-03-front.png";
import level04 from "../../assets/pictograms/peeka-battery-level-04-front.png";
import level05 from "../../assets/pictograms/peeka-battery-level-05-front.png";
import level06 from "../../assets/pictograms/peeka-battery-level-06-front.png";
import level07 from "../../assets/pictograms/peeka-battery-level-07-front.png";
import level08 from "../../assets/pictograms/peeka-battery-level-08-front.png";
import level09 from "../../assets/pictograms/peeka-battery-level-09-front.png";
import level10 from "../../assets/pictograms/peeka-battery-level-10-front.png";

/* 01 은 빈 배터리라 안 쓴다 — batteryLevel 주석 참조 */
const LEVEL_SRC: Record<number, string> = {
  2: level02,
  3: level03,
  4: level04,
  5: level05,
  6: level06,
  7: level07,
  8: level08,
  9: level09,
  10: level10,
};

export interface BatteryPictogramProps {
  /** 지금 내 건전지 수 */
  count: number;
  /** 'sheet' 96px — 후원 시트 머리 · 'cheer' 160px — 넣은 직후 축하 */
  size?: "sheet" | "cheer";
}

export default function BatteryPictogram({ count, size = "sheet" }: BatteryPictogramProps) {
  const level = batteryLevel(count);
  if (level === null) return null;

  return (
    <img
      className={`${styles.pictogram} ${size === "cheer" ? styles.cheer : styles.sheet}`}
      src={LEVEL_SRC[level]}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}
